import type { CheckoutSimple } from "@commercekit/api";
import {
  useCustomerSimple,
  useLocale,
  useLocalePath,
} from "@commercekit/remix";
import {
  fallbackMoney,
  formatMoneyWithCurrency,
  resolveCurrencyCode,
} from "@commercekit/shared";
import { Popover } from "@headlessui/react";
import { useNavigate } from "@remix-run/react";
import classNames from "classnames";
import { useMemo } from "react";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import type { HTMLComponent } from "~/types/HTMLComponent";
import WvLink from "../atoms/WvLink";
import { optimizeImageUrl } from "~/lib/imageUtils";

const CartEmpty: HTMLComponent<{
  close: () => void;
}> = ({ close }) => {
  const { $pt } = usePrismicLabels();
  return (
    <div className="cart-popup-container">
      <div className="cart-empty-content">
        <h3 className="heading strong sans-serif xl">
          {" "}
          {$pt("shopping_cart_popup_title")}
        </h3>
        <p className="cart-subtitle">{$pt("shopping_cart_popup_empty_cart")}</p>
        <button
          type="button"
          className="mt-3 cart-back-btn"
          onClick={() => close()}
        >
          {$pt("shopping_cart_popup_back")}
        </button>
      </div>
    </div>
  );
};

type PreviewLine = CheckoutSimple["lineItems"][number];

// originalTotal only exists incl. VAT; scale it by the line's own excl/incl ratio.
const scaleToExclTax = (item: PreviewLine) => {
  if (!item.originalTotal) return item.originalTotal;
  const incl = item.totalInclTax?.value;
  const excl = item.totalExclTax?.value;
  if (!incl || excl == null) return item.originalTotal;
  return {
    ...item.originalTotal,
    value: (item.originalTotal.value * excl) / incl,
  };
};

const PopCart: HTMLComponent<{
  checkoutSimple: CheckoutSimple | null;
  tempCart: CheckoutSimple | null;
  close: () => void;
  open: boolean;
}> = ({ checkoutSimple, tempCart, close }) => {
  const { customer } = useCustomerSimple();
  const { localeFullName } = useLocale();
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();
  const navigate = useNavigate();

  const cart = useMemo(
    () => tempCart ?? checkoutSimple,
    [tempCart, checkoutSimple]
  );

  const isTFCustomer = useMemo(
    () => customer?.type.toLowerCase().endsWith("tf") ?? false,
    [customer]
  );

  const subTotal = useMemo(() => {
    if (!checkoutSimple) return fallbackMoney();
    const total = isTFCustomer
      ? checkoutSimple?.subtotalExclTax?.value
      : checkoutSimple?.subtotalInclTax?.value;
    return {
      value: total,
      currencyCode: checkoutSimple?.subtotalExclTax?.currencyCode,
    };
  }, [checkoutSimple, isTFCustomer]);

  // ISO code shown inside the total label, e.g. "Totaal (EUR)".
  const currency = resolveCurrencyCode(localeFullName, subTotal?.currencyCode);

  if (!cart?.lineItems) {
    return <CartEmpty close={close} />;
  }

  return (
    <div className="cart-popup-container">
      {/* Header */}
      <div className="cart-popup-header">
        <div>
          <h3 className="heading strong sans-serif xl">
            {$pt("shopping_cart_popup_title")}
          </h3>
          <p className="cart-subtitle">
            <strong>{cart?.lineItems.length || 0}</strong>
            {$pt("shopping_cart_popup_out_of")}{" "}
            <strong>{cart?.lineItems.length || 0}</strong>
            {$pt("shopping_cart_popup_articles_shown")}
          </p>
        </div>

        <button
          type="button"
          className="cart-close-btn"
          onClick={() => close()}
          aria-label="Close cart"
        >
          ×
        </button>
      </div>

      {/* Product List */}
      <div className="cart-products-section">
        {cart?.lineItems.map((item, index) => {
          // Same as the total below ex-VAT for TF users, inc-VAT for others
          const linePrice =
            (isTFCustomer ? item.totalExclTax : item.totalInclTax)
            ?? item.total;

          const lineOriginal = isTFCustomer
            ? scaleToExclTax(item)
            : item.originalTotal;

          return (
            <div key={`${item.id}_${index}`} className="cart-product-item">
              <div className="cart-product-quantity">{item.quantity}x</div>
              <div className="cart-product-image">
                <WvLink to={localePath(`/${item?.product?.slug ?? 404}`)}>
                  <img
                    src={optimizeImageUrl(item?.image?.url || "")}
                    alt={item?.image?.altText || ""}
                    width="50"
                    height="50"
                  />
                </WvLink>
              </div>
              <div className="cart-product-details">
                <WvLink
                  to={localePath(`/${item?.product?.slug ?? 404}`)}
                  className="cart-product-name"
                >
                  {item.name || `Product ${item.sku}`}
                </WvLink>
              </div>
              <div className="cart-product-pricing">
                {lineOriginal && lineOriginal.value !== linePrice?.value && (
                  <span className="cart-product-original-price">
                    {formatMoneyWithCurrency(localeFullName, lineOriginal)}
                  </span>
                )}
                <span className="cart-product-current-price">
                  {formatMoneyWithCurrency(localeFullName, linePrice)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Invalid items warning */}
      {cart.lineItems.some((v) => !v.valid) && (
        <div className="cart-warning">
          <p className="cart-warning-text">
            {$pt("invalid_line_items_message")}
          </p>
        </div>
      )}

      {/* Total Section */}
      <div className="cart-total-section">
        {tempCart ? (
          <div className="cart-loading">
            <i className="zmdi-hc-spin large" data-icon-after=""></i>
          </div>
        ) : (
          <div className="cart-total">
            <div className="cart-total-amount">
              <strong>
                {formatMoneyWithCurrency(localeFullName, subTotal)}
              </strong>
            </div>
            <div className="mt-1 cart-total-label vat">
              {isTFCustomer
                ? $pt("shopping_cart_summary_total_exc_vat", { currency })
                : $pt("shopping_cart_popup_total", { currency })}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="cart-actions">
        <button type="button" className="cart-back-btn" onClick={() => close()}>
          {$pt("shopping_cart_popup_back")}
        </button>
        <button
          className="cart-checkout-btn"
          onClick={() => {
            close();
            navigate(localePath("/cart/"));
          }}
        >
          <svg width="25" height="23" xmlns="http://www.w3.org/2000/svg">
            <g fill="currentcolor" fillRule="nonzero">
              <path d="M8.53 18.882a1.882 1.882 0 100 3.765 1.882 1.882 0 000-3.765zM21.706 18.882a1.882 1.882 0 100 3.765 1.882 1.882 0 000-3.765zM4.765.059c.45 0 .836.318.923.757l.004.02L8.378 16.06h14.27c.513 0 .93.411.94.922V17a.941.941 0 01-.922.941H7.588a.941.941 0 01-.923-.757l-.004-.02L3.975 1.94H1a.941.941 0 01-.941-.922V1C.059.486.47.069.98.059H4.765z"></path>
              <path d="M23.673 3.864c.636-.205 1.338.338 1.21 1.063l-.005.022-1.694 8.47a.941.941 0 01-.902.757H7.588a.941.941 0 01-.018-1.882h13.92l1.317-6.588H5.706a.941.941 0 01-.941-.923v-.018c0-.514.411-.931.922-.941h17.705c.09 0 .178.012.263.037l.014.004h.004z"></path>
            </g>
          </svg>
          <span>{$pt("shopping_cart_popup_goto_cart")} </span>

          {/* <svg
            width="7"
            height="12"
            viewBox="0 0 7 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L6 6L1 11"
              stroke="currentcolor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg> */}
        </button>
      </div>
    </div>
  );
};

const WvPopupShoppingCart: HTMLComponent<{
  popoverButton: React.ReactNode;
  checkoutSimple: CheckoutSimple | null;
  tempCart: CheckoutSimple | null;
}> = ({ popoverButton, checkoutSimple, tempCart }) => {
  return (
    <Popover className="cart-popover-wrapper">
      {({ open, close }) => (
        <>
          <Popover.Button
            aria-label="Shopping Cart"
            className={classNames("cart-trigger-button", open ? "active" : "")}
          >
            {popoverButton}
          </Popover.Button>

          {/* Backdrop overlay */}
          {open && <div className="cart-popup-backdrop" onClick={close} />}

          <Popover.Panel
            static
            className={classNames(
              "cart-popup-panel",
              open ? "active" : "",
              !(tempCart ?? checkoutSimple)?.lineItems ? "empty" : ""
            )}
          >
            <PopCart
              close={close}
              checkoutSimple={checkoutSimple}
              tempCart={tempCart}
              open={open}
            />
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
};

export default WvPopupShoppingCart;
